import React, { FC } from 'react'
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  IconButton
} from '@material-ui/core'
import { ReactComponent as CloseIcon } from 'assets/close.svg'
import { Modal } from 'components/modal'

interface AboutEventModalProps {
  onClose: () => void
  open: boolean
}

export const AboutEventModal: FC<AboutEventModalProps> = ({ open, onClose }) => {
  const classes = useStyles()

  return (
    <Modal onClose={onClose} open={open} className={classes.modal}>
      <Box className={classes.container}>
        <Paper className={classes.paper} square>
          <IconButton aria-label='close' className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Grid container>
            <Grid item sm={4} classes={{ root: classes.introContainer }}>
              <Typography component='h5' variant='h5' className={classes.sectionTitle}>
                About the event.
              </Typography>
            </Grid>
            <Grid item sm={8} classes={{ root: classes.introContainer }}>
              <Typography paragraph>
                View our virtual customer event, which brings together experts from Verizon and groundbreaking companies
                to explore the role of private 5G in business.
                <br />
                <br />
                Keynote speakers discuss topics that include Industry 4.0, and the innovations that 5G enables, such as
                predictive maintenance, remote operations, improving safety and increasing operational efficiency.
                <br />
                <br />
                You’ll hear how other companies are already innovating with 5G, and how you can apply the lessons they
                have learned to your business. You can also experience what is possible with our demo showcase.
                <br />
                <br />
                This is the 5G you have been waiting for. This is 5G built right for business – your platform for
                innovation.
              </Typography>
            </Grid>
          </Grid>
          <Grid container className={classes.eventAgendaContainer}>
            <Grid item sm={4}>
              <Typography component='h5' variant='h5' className={classes.sectionTitle}>
                Event agenda.
              </Typography>
            </Grid>

            <Grid item xs={8}>
              <Table className={classes.table}>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant='h6' component='h6' className={classes.agendaItemTitle}>
                        Welcome to the event
                      </Typography>
                      <Typography paragraph className={classes.presenter}>
                        Tami Erwin, CEO, Verizon Business
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography variant='h6' component='h6' className={classes.agendaItemTitle}>
                        Welcome chat
                      </Typography>
                      <Typography paragraph className={classes.presenter}>
                        Hosted by Lianna Brinded, Head of Yahoo Finance UK
                      </Typography>

                      <Typography paragraph variant='caption' className={classes.itemDetails}>
                        Jan van Tetering, SVP Europe, Nokia; and Tony Judd, MD UK&I and acting MD EMEA, Verizon
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography variant='h6' component='h6' className={classes.agendaItemTitle}>
                        Manufacturing industry panel discussion
                      </Typography>
                      <Typography paragraph className={classes.presenter}>
                        John Crawford, GM 5G International, Verizon
                      </Typography>

                      <Typography paragraph variant='caption' className={classes.itemDetails}>
                        Jen Hunt, Digital Innovation Director, Unipart Logistics; Geraldina Iraheta, Director Of
                        Business Development, Digital Catapult; Jani Virtanen, Head of Digital Transformation &amp; IT
                        at Supply Center, Bayer
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography variant='h6' component='h6' className={classes.agendaItemTitle}>
                        Keynote 1
                      </Typography>

                      <Typography paragraph className={classes.presenter}>
                        Roland Sillmann, CEO, WISTA Management
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography variant='h6' component='h6' className={classes.agendaItemTitle}>
                        Keynote 2
                      </Typography>

                      <Typography paragraph className={classes.presenter}>
                        Antonio Franchi, Head of Future Programme Acquisition, Satellite Constellations, 5G &amp; AI,
                        European Space Agency
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell style={{ borderBottom: 0 }}>
                      <Typography variant='h6' component='h6' className={classes.agendaItemTitle}>
                        5G private networks from Verizon
                      </Typography>

                      <Typography paragraph className={classes.presenter}>
                        Cormac Whelan, CEO UK &amp; Ireland, Nokia; Toby Redshaw, SVP Enterprise Innovation and 5G
                        Solutions
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Modal>
  )
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modal: {
      alignItems: 'unset !important',
      [theme.breakpoints.down('xs')]: {
        '& > div': {
          width: '100vw'
        }
      }
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(3),
      right: theme.spacing(3)
    },
    paper: {
      padding: theme.spacing(8, 10),
      position: 'relative',
      width: '100%',

      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(8, 4)
      }
    },
    container: {
      overflowY: 'scroll',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    eventAgendaContainer: {
      marginTop: theme.spacing(6)
    },
    introContainer: {
      marginTop: 13
    },
    agendaItemTitle: {
      fontSize: 18,
      marginBottom: 7,
      lineHeight: '16.8px'
    },
    presenter: {
      fontSize: 16,
      lineHeight: '19.15px',
      marginBottom: 10
    },
    itemDetails: {
      fontSize: 14,
      lineHeight: '16.76px'
    },
    sectionTitle: {
      fontSize: 28,
      '&:after': {
        backgroundColor: '#d52b1e',
        content: '" "',
        height: 4,
        marginTop: theme.spacing(1),
        width: 32,
        display: 'block'
      }
    },
    table: {
      borderTop: '4px solid #000000'
    },
    tableSchedule: {
      minWidth: 210,
      verticalAlign: 'top'
    },
    footer: {
      backgroundColor: '#F6F6F6'
    }
  })
)
